import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BanknotesIcon,
  ArchiveBoxIcon,
  ShieldExclamationIcon,
  TruckIcon,
  ExclamationTriangleIcon,
  ShoppingCartIcon,
  EyeIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useAppData } from '../contexts/AppDataContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const navigate = useNavigate();
  // Utiliser le contexte AppData pour accéder aux données partagées
  const { 
    globalStats, 
    getOrders, 
    getLowStockProducts, 
    products, 
    suppliers,
    getStockItems,
    purchases,
    sales,
    warehouses,
    stockItems: allStockItems,
    fetchAllData
  } = useAppData();
  
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Récupérer les données nécessaires
  const orders = getOrders();
  console.log('Dashboard: Orders data:', {
    ordersCount: orders.length,
    sampleOrder: orders[0],
    orderStatuses: orders.map(o => o.status)
  });
  const lowStockProducts = getLowStockProducts();
  const stockItems = getStockItems();
  
  // Refresh data function
  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      await fetchAllData();
      toast.success('Données actualisées avec succès');
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error('Erreur lors de l\'actualisation des données');
    } finally {
      setIsRefreshing(false);
    }
  };
  
  // Calculate real-time stats from actual data
  const calculateStats = () => {
    // Calculate total stock from products
    const totalStock = products.reduce((sum, product) => sum + (parseInt(product.stock) || 0), 0);
    
    // Calculate revenue from sales
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    
    // Debug sales data
    console.log('Dashboard: Sales data available:', {
      totalSales: sales.length,
      sampleSale: sales[0],
      allSales: sales
    });
    
    // Filter sales for current month and last month
    const currentMonthSales = sales.filter(sale => {
      if (!sale.date && !sale.createdAt) return false;
      const saleDate = new Date(sale.date || sale.createdAt);
      return saleDate.getMonth() === currentMonth && saleDate.getFullYear() === currentYear;
    });
    
    const lastMonthSales = sales.filter(sale => {
      if (!sale.date && !sale.createdAt) return false;
      const saleDate = new Date(sale.date || sale.createdAt);
      return saleDate.getMonth() === lastMonth && saleDate.getFullYear() === lastMonthYear;
    });
    
    console.log('Dashboard: Filtered sales:', {
      currentMonth: `${currentMonth+1}/${currentYear}`,
      currentMonthSales: currentMonthSales.length,
      lastMonth: `${lastMonth+1}/${lastMonthYear}`,
      lastMonthSales: lastMonthSales.length
    });
    
    // Calculate revenue - check for different possible structures
    const currentMonthRevenue = currentMonthSales.reduce((sum, sale) => {
      // Try to get total from different possible properties
      let saleTotal = 0;
      
      if (sale.items && Array.isArray(sale.items) && sale.items.length > 0) {
        // Calculate from items if available
        saleTotal = sale.items.reduce((itemSum, item) => {
          const itemTotal = Number(item.total) || (Number(item.quantity || 0) * Number(item.price || 0));
          return itemSum + itemTotal;
        }, 0);
      } else if (sale.total && !isNaN(parseFloat(sale.total))) {
        // Use total property if available
        saleTotal = parseFloat(sale.total);
      } else if (sale.amount && !isNaN(parseFloat(sale.amount.replace(/[^0-9.-]+/g,'')))) {
        // Try to parse from formatted amount
        saleTotal = parseFloat(sale.amount.replace(/[^0-9.-]+/g,''));
      }
      
      return sum + saleTotal;
    }, 0);
    
    const lastMonthRevenue = lastMonthSales.reduce((sum, sale) => {
      // Try to get total from different possible properties
      let saleTotal = 0;
      
      if (sale.items && Array.isArray(sale.items) && sale.items.length > 0) {
        // Calculate from items if available
        saleTotal = sale.items.reduce((itemSum, item) => {
          const itemTotal = Number(item.total) || (Number(item.quantity || 0) * Number(item.price || 0));
          return itemSum + itemTotal;
        }, 0);
      } else if (sale.total && !isNaN(parseFloat(sale.total))) {
        // Use total property if available
        saleTotal = parseFloat(sale.total);
      } else if (sale.amount && !isNaN(parseFloat(sale.amount.replace(/[^0-9.-]+/g,'')))) {
        // Try to parse from formatted amount
        saleTotal = parseFloat(sale.amount.replace(/[^0-9.-]+/g,''));
      }
      
      return sum + saleTotal;
    }, 0);
    
    console.log('Dashboard: Calculated revenue:', {
      currentMonthRevenue,
      lastMonthRevenue
    });
    
    // Calculate revenue change percentage
    let revenueChangePercent = 0;
    if (lastMonthRevenue > 0) {
      revenueChangePercent = ((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100;
    }
    
    // Count low stock products
    const lowStockCount = lowStockProducts.length;
    
    // Count pending orders - fixed to properly check for string values
    // Some orders might have status as undefined or different case
    const pendingOrders = orders.filter(order => {
      const status = order.status ? order.status.toLowerCase() : '';
      return status === 'en attente' || status === 'en cours' || status === 'confirmé';
    }).length;
    
    console.log('Dashboard: Pending orders calculation:', {
      pendingOrders,
      pendingOrdersList: orders.filter(order => {
        const status = order.status ? order.status.toLowerCase() : '';
        return status === 'en attente' || status === 'en cours' || status === 'confirmé';
      }),
      allOrders: orders
    });
    
    return [
      {
        name: 'Produits en stock',
        value: `${totalStock}`,
        change: `${products.length} produits`,
        changeType: 'neutral',
        icon: ArchiveBoxIcon,
        color: 'from-blue-500 to-blue-600',
      },
      {
        name: 'Ventes du mois',
        value: `${currentMonthRevenue.toLocaleString('fr-DZ')} DA`,
        change: revenueChangePercent > 0 
          ? `+${revenueChangePercent.toFixed(1)}%` 
          : `${revenueChangePercent.toFixed(1)}%`,
        changeType: revenueChangePercent >= 0 ? 'positive' : 'negative',
        icon: BanknotesIcon,
        color: 'from-green-500 to-green-600',
      },
      {
        name: 'Produits en alerte',
        value: `${lowStockCount}`,
        change: `${Math.round(lowStockCount / Math.max(products.length, 1) * 100)}% du stock`,
        changeType: lowStockCount > 0 ? 'negative' : 'positive',
        icon: ExclamationTriangleIcon,
        color: 'from-amber-500 to-amber-600',
      },
      {
        name: 'Commandes en attente',
        value: `${orders.length}`,
        change: `${orders.length} commandes totales`,
        changeType: 'neutral',
        icon: ShoppingCartIcon,
        color: 'from-purple-500 to-purple-600',
      },
    ];
  };

  // Stats data based on real-time calculations
  const stats = calculateStats();

  // Commandes récentes - utilisation des vraies données
  const recentOrders = orders.slice(0, 4).map(order => {
    // Normalize status for consistent display
    const normalizedStatus = (order.status || "en attente").toLowerCase();
    
    return {
    id: order.id,
    customer: order.customer || "Client inconnu",
    date: order.date || new Date().toISOString().split('T')[0],
    amount: `${(order.total !== undefined && order.total !== null) ? order.total.toLocaleString('fr-DZ') : '0'} DA`,
      status: normalizedStatus,
    statusColor: 
        normalizedStatus === 'livré' || normalizedStatus === 'payé' 
        ? 'bg-green-500/20 text-green-400'
          : normalizedStatus === 'en cours'
        ? 'bg-blue-500/20 text-blue-400'
        : 'bg-amber-500/20 text-amber-400',
    };
  });

  // Produits à faible stock
  const formattedLowStockProducts = lowStockProducts.slice(0, 4).map(product => ({
    id: product.id,
    name: product.name,
    quantity: product.stock,
    minimum: product.lowStockThreshold || 5,
    status: product.stock <= (product.lowStockThreshold || 5) * 0.5 ? 'Critique' : 'Faible',
    statusColor: product.stock <= (product.lowStockThreshold || 5) * 0.5 
      ? 'bg-red-500/20 text-red-400' 
      : 'bg-amber-500/20 text-amber-400',
  })) || [];

  // Format date helper function
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = ['Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'][date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };
  
  // Get upcoming deliveries from actual purchase data
  const getUpcomingDeliveries = () => {
    const today = new Date();
    
    // Filter purchases that are pending or in progress
    const pendingPurchases = purchases.filter(purchase => {
      // Check if purchase has a delivery date in the future
      if (!purchase.deliveryDate) return false;
      
      const deliveryDate = new Date(purchase.deliveryDate);
      const isPending = purchase.status === 'en attente' || 
                        purchase.status === 'confirmé' ||
                        purchase.status === 'en cours' ||
                        purchase.status === 'en transit';
                        
      return isPending && deliveryDate >= today;
    });
    
    // Sort by delivery date (ascending)
    pendingPurchases.sort((a, b) => {
      const dateA = new Date(a.deliveryDate || a.date);
      const dateB = new Date(b.deliveryDate || b.date);
      return dateA - dateB;
    });
    
    // Get the 3 most imminent deliveries
    return pendingPurchases.slice(0, 3).map(purchase => {
      // Find supplier name
      const supplier = suppliers.find(s => s.id === purchase.supplierId);
      const supplierName = supplier ? supplier.name : 'Fournisseur inconnu';
      
      // Count products in this purchase
      const productCount = purchase.items ? purchase.items.length : 
                          (purchase.products ? purchase.products.length : 0);
      
      // Map status to more user-friendly terms
      let status = purchase.status;
      if (status === 'en attente') status = 'Planifié';
      if (status === 'confirmé') status = 'Confirmé';
      if (status === 'en cours' || status === 'en transit') status = 'En transit';
      
      return {
        id: purchase.id,
        supplier: supplierName,
        date: formatDate(purchase.deliveryDate || purchase.date),
        products: productCount,
        status: status
      };
    });
  };
  
  // Livraisons à venir - basées sur les données réelles d'achats
  const upcomingDeliveries = getUpcomingDeliveries();
  
  // Set up event listeners for real-time updates
  useEffect(() => {
    // Universal handler for all data update events
    const handleDataUpdate = (event) => {
      console.log(`Dashboard: ${event.type} event detected, refreshing data`);
      
      // Force direct refresh from localStorage first for instant updates
      try {
        // Get fresh data directly from localStorage for immediate UI update
        const productsData = localStorage.getItem('stockpro_products');
        const suppliersData = localStorage.getItem('stockpro_suppliers');
        const customersData = localStorage.getItem('stockpro_customers');
        const salesData = localStorage.getItem('stockpro_sales');
        const purchasesData = localStorage.getItem('stockpro_purchases');
        const warehousesData = localStorage.getItem('stockpro_warehouses');
        const stockData = localStorage.getItem('stockpro_stock');
        
        if (productsData || suppliersData || customersData || salesData || 
            purchasesData || warehousesData || stockData) {
          console.log('Dashboard: Loaded fresh data from localStorage');
        }
      } catch (error) {
        console.error('Error accessing localStorage data:', error);
      }
      
      // Trigger a refresh of the dashboard data
      setRefreshTrigger(prev => prev + 1);
    };
    
    // Add event listeners for all possible data change events
    window.addEventListener('product-stock-updated', handleDataUpdate);
    window.addEventListener('transfer-completed', handleDataUpdate);
    window.addEventListener('warehouse-inventory-updated', handleDataUpdate);
    window.addEventListener('refresh-data', handleDataUpdate);
    window.addEventListener('product-added', handleDataUpdate);
    window.addEventListener('product-updated', handleDataUpdate);
    window.addEventListener('product-deleted', handleDataUpdate);
    window.addEventListener('sale-added', handleDataUpdate);
    window.addEventListener('sale-updated', handleDataUpdate);
    window.addEventListener('purchase-added', handleDataUpdate);
    window.addEventListener('purchase-updated', handleDataUpdate);
    window.addEventListener('order-added', handleDataUpdate);
    window.addEventListener('order-updated', handleDataUpdate);
    window.addEventListener('order-deleted', handleDataUpdate);
    
    // Clean up event listeners
    return () => {
      window.removeEventListener('product-stock-updated', handleDataUpdate);
      window.removeEventListener('transfer-completed', handleDataUpdate);
      window.removeEventListener('warehouse-inventory-updated', handleDataUpdate);
      window.removeEventListener('refresh-data', handleDataUpdate);
      window.removeEventListener('product-added', handleDataUpdate);
      window.removeEventListener('product-updated', handleDataUpdate);
      window.removeEventListener('product-deleted', handleDataUpdate);
      window.removeEventListener('sale-added', handleDataUpdate);
      window.removeEventListener('sale-updated', handleDataUpdate);
      window.removeEventListener('purchase-added', handleDataUpdate);
      window.removeEventListener('purchase-updated', handleDataUpdate);
      window.removeEventListener('order-added', handleDataUpdate);
      window.removeEventListener('order-updated', handleDataUpdate);
      window.removeEventListener('order-deleted', handleDataUpdate);
    };
  }, []);
  
  // Update data when refreshTrigger changes
  useEffect(() => {
    const loadData = async () => {
      console.log('Dashboard: Loading fresh data...');
      try {
        // First, get fresh data from localStorage
        try {
          const productsData = localStorage.getItem('stockpro_products');
          const suppliersData = localStorage.getItem('stockpro_suppliers');
          const customersData = localStorage.getItem('stockpro_customers');
          const salesData = localStorage.getItem('stockpro_sales');
          const purchasesData = localStorage.getItem('stockpro_purchases');
          const warehousesData = localStorage.getItem('stockpro_warehouses');
          const stockData = localStorage.getItem('stockpro_stock');
          
          // Parse and use the data directly for immediate UI update
          if (productsData) {
            const freshProducts = JSON.parse(productsData);
            console.log(`Dashboard: Loaded ${freshProducts.length} products from localStorage`);
          }
          if (salesData) {
            const freshSales = JSON.parse(salesData);
            console.log(`Dashboard: Loaded ${freshSales.length} sales from localStorage`);
          }
          if (purchasesData) {
            const freshPurchases = JSON.parse(purchasesData);
            console.log(`Dashboard: Loaded ${freshPurchases.length} purchases from localStorage`);
          }
        } catch (localStorageError) {
          console.error('Error parsing localStorage data:', localStorageError);
        }
        
        // Then fetch all data from the context
        await fetchAllData();
        console.log('Dashboard: Data refresh completed successfully');
      } catch (error) {
        console.error('Error refreshing dashboard data:', error);
      }
    };
    
    loadData();
  }, [refreshTrigger, fetchAllData]);

  return (
    <div className="space-y-6">
      {/* Page Title */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Tableau de bord</h1>
          <p className="text-white/60 mt-2">Aperçu de votre inventaire et activité récente</p>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={refreshData}
            disabled={isRefreshing}
            className="bg-gradient-to-r from-green-600 to-teal-600 text-white px-4 py-2 rounded-xl font-medium hover:from-green-700 hover:to-teal-700 transition-all duration-300 flex items-center space-x-2 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span>{isRefreshing ? 'Actualisation...' : 'Actualiser'}</span>
          </button>
          <button 
            onClick={() => navigate('/reports')}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
          >
            <span>Voir les rapports</span>
            <EyeIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div 
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 hover:border-white/30 transition-all duration-300"
          >
            <div className="flex justify-between items-center">
              <div>
                <p className="text-white/60 text-sm font-medium">{stat.name}</p>
                <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="mt-4 flex items-center">
              {stat.changeType === 'positive' ? (
                <ArrowUpIcon className="h-4 w-4 text-green-400 mr-1" />
              ) : stat.changeType === 'negative' ? (
                <ArrowDownIcon className="h-4 w-4 text-red-400 mr-1" />
              ) : null}
              <span className={`text-sm ${
                stat.changeType === 'positive' 
                  ? 'text-green-400' 
                  : stat.changeType === 'negative' 
                    ? 'text-red-400' 
                    : 'text-white/60'
              }`}>
                {stat.change} {stat.changeType !== 'neutral' && 'depuis le mois dernier'}
              </span>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Commandes récentes */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden"
        >
          <div className="p-6 border-b border-white/10">
            <h2 className="text-xl font-semibold text-white">Commandes récentes</h2>
          </div>
          <div className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-4 text-left text-white/70 font-medium">ID</th>
                    <th className="px-6 py-4 text-left text-white/70 font-medium">Client</th>
                    <th className="px-6 py-4 text-left text-white/70 font-medium">Date</th>
                    <th className="px-6 py-4 text-left text-white/70 font-medium">Montant</th>
                    <th className="px-6 py-4 text-left text-white/70 font-medium">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {recentOrders.map((order) => (
                    <motion.tr 
                      key={order.id} 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-white/5 transition-colors"
                    >
                      <td className="px-6 py-4 text-white font-mono text-sm">{order.id}</td>
                      <td className="px-6 py-4 text-white font-medium">{order.customer}</td>
                      <td className="px-6 py-4 text-white/80">{order.date}</td>
                      <td className="px-6 py-4 text-white font-medium">{order.amount}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${order.statusColor}`}>
                          {order.status}
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 text-center border-t border-white/10">
              <button className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors">
                Voir toutes les commandes
              </button>
            </div>
          </div>
        </motion.div>

        {/* Produits à faible stock */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden"
        >
          <div className="p-6 border-b border-white/10">
            <h2 className="text-xl font-semibold text-white">Alertes de stock</h2>
          </div>
          <div className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-4 text-left text-white/70 font-medium">ID</th>
                    <th className="px-6 py-4 text-left text-white/70 font-medium">Produit</th>
                    <th className="px-6 py-4 text-left text-white/70 font-medium">Quantité</th>
                    <th className="px-6 py-4 text-left text-white/70 font-medium">Minimum</th>
                    <th className="px-6 py-4 text-left text-white/70 font-medium">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {formattedLowStockProducts.map((product) => (
                    <motion.tr 
                      key={product.id} 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-white/5 transition-colors"
                    >
                      <td className="px-6 py-4 text-white font-mono text-sm">{product.id}</td>
                      <td className="px-6 py-4 text-white font-medium">{product.name}</td>
                      <td className="px-6 py-4 text-white">{product.quantity}</td>
                      <td className="px-6 py-4 text-white/80">{product.minimum}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${product.statusColor}`}>
                          {product.status}
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 text-center border-t border-white/10">
              <button className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors">
                Gérer le stock
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Livraisons à venir */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden"
      >
        <div className="p-6 border-b border-white/10">
          <h2 className="text-xl font-semibold text-white">Livraisons à venir</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {upcomingDeliveries.map((delivery, index) => (
              <motion.div 
                key={delivery.id} 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="bg-white/5 rounded-2xl p-5 border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all duration-300"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-white font-medium">{delivery.supplier}</p>
                    <p className="text-white/60 text-sm">ID: {delivery.id}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600">
                    <TruckIcon className="h-5 w-5 text-white" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <p className="text-white/60">Date de livraison:</p>
                    <p className="text-white">{delivery.date}</p>
                  </div>
                  <div className="flex justify-between text-sm">
                    <p className="text-white/60">Nombre de produits:</p>
                    <p className="text-white">{delivery.products}</p>
                  </div>
                  <div className="flex justify-between text-sm">
                    <p className="text-white/60">Statut:</p>
                    <p className="text-white">{delivery.status}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard; 